import { useMemo, useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import {
  ChevronDown, ChevronRight, Search, X, CheckCircle2, Plus, BookOpen,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useControlLibrary, useAddControlToWorkspace, type LibraryControl,
} from '@/api/hooks/useControlLibrary';

/**
 * CONTROL LIBRARY — browse the published VeriSAQ catalogue and add controls directly.
 *
 * ⚠ THE POINT OF THIS PAGE IS THE DECOUPLING. A Compliance licence entitles a tenant to the whole
 * published canonical library; framework licensing and adoption are a SEPARATE layer that brings
 * requirements and their mappings. Until this slice, adoption was the ONLY way a control could
 * enter a workspace — not by design, but because no other door had ever been built, and a tenant
 * retro found the Controls page unusable without adopting a framework they might not be licensed
 * for.
 *
 * ⚠ NO FRAMEWORK LENS IN v1, DELIBERATELY. Mappings may eventually help navigate this page, but a
 * framework-shaped filter is exactly where a mis-render would imply framework-as-gate — the thing
 * this page exists to disprove. v1 filters by the catalogue's own structure and by search.
 */

/** ⚠ ONE grid template, used by the column header AND every row, so the columns actually align. */
const GRID = 'grid grid-cols-[118px_minmax(0,1fr)_118px_150px_172px] gap-4';

/**
 * The library's route. A control's DETAIL WINDOW is its child, `${CONTROL_LIBRARY_PATH}/:reference`, rendered
 * through this page's <Outlet/> — so the list (and its search and filters) stays mounted beneath the window.
 */
export const CONTROL_LIBRARY_PATH = '/compliance/control-library';

interface Family { code: string; name: string; rows: LibraryControl[]; added: number }
interface Group { code: string; name: string; families: Family[]; total: number; added: number }

/** Domain → Family → controls, from the flat API rows. Order is the API's; this never re-sorts. */
function group(rows: LibraryControl[]): Group[] {
  const domains = new Map<string, Group>();
  for (const r of rows) {
    let d = domains.get(r.domain_public_id);
    if (!d) {
      d = { code: r.domain_public_id, name: r.domain_name, families: [], total: 0, added: 0 };
      domains.set(r.domain_public_id, d);
    }
    let f = d.families.find((x) => x.code === r.family_public_id);
    if (!f) {
      f = { code: r.family_public_id, name: r.family_name, rows: [], added: 0 };
      d.families.push(f);
    }
    f.rows.push(r);
    d.total += 1;
    if (r.in_workspace) { d.added += 1; f.added += 1; }
  }
  return [...domains.values()];
}

export default function ControlLibraryPage() {
  const [search, setSearch] = useState('');
  const [domain, setDomain] = useState('');
  const [family, setFamily] = useState('');
  const [workspace, setWorkspace] = useState<'all' | 'in' | 'available'>('all');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const { data, isLoading } = useControlLibrary({
    search: search.trim() || undefined,
    domain: domain || undefined,
    family: family || undefined,
  });
  const add = useAddControlToWorkspace();
  const navigate = useNavigate();
  // ⚠ By the DURABLE reference — the URL key — never the display code, which is a mutable label.
  const openControl = (reference: string) =>
    navigate(`${CONTROL_LIBRARY_PATH}/${encodeURIComponent(reference)}`);

  const all = useMemo(() => data?.rows ?? [], [data]);

  // ⚠ The workspace filter is applied CLIENT-side on purpose: it is a view of rows already
  //    fetched, and pushing it to the server would make the "N controls match" line disagree with
  //    the counts in the group headers, which come from the same fetch.
  const rows = useMemo(
    () => all.filter((r) => workspace === 'all'
      || (workspace === 'in' ? r.in_workspace : !r.in_workspace)),
    [all, workspace],
  );

  const groups = useMemo(() => group(rows), [rows]);
  const inWorkspace = all.filter((r) => r.in_workspace).length;
  const filtered = Boolean(search.trim() || domain || family || workspace !== 'all');

  const domainOptions = useMemo(() => {
    const m = new Map<string, string>();
    all.forEach((r) => m.set(r.domain_public_id, r.domain_name));
    return [...m.entries()];
  }, [all]);

  const familyOptions = useMemo(() => {
    const m = new Map<string, string>();
    all.filter((r) => !domain || r.domain_public_id === domain)
      .forEach((r) => m.set(r.family_public_id, r.family_name));
    return [...m.entries()];
  }, [all, domain]);

  const reset = () => { setSearch(''); setDomain(''); setFamily(''); setWorkspace('all'); };

  return (
    <div className="p-8 pb-20" data-testid="control-library-page">
      <div className="mb-1 font-mono text-[11px] uppercase tracking-[0.14em] text-text-faint">
        app.verigrc.com / compliance / control-library
      </div>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[30px] font-semibold tracking-tight text-text-primary">
            Control Library
          </h1>
          <p className="mt-2 max-w-prose text-[13.5px] text-text-secondary">
            Browse published VeriSAQ controls and add them to your workspace. Added controls appear
            in{' '}
            <Link to="/compliance/workspace/controls" className="text-accent-cyan hover:underline">
              My Controls
            </Link>{' '}
            for assessment, evidence, and ongoing compliance work.
          </p>
        </div>
        {/* ⚠ A labelled pill, not a bare number — "3" beside a heading is a riddle. */}
        <div className="flex shrink-0 items-center gap-2 rounded-full border border-border-subtle bg-white/[0.04] px-3 py-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-cert-valid-fg" aria-hidden="true" />
          <span className="text-[12.5px] text-text-secondary">
            {inWorkspace} control{inWorkspace === 1 ? '' : 's'} in workspace
          </span>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-faint"
            aria-hidden="true"
          />
          <input
            aria-label="Search controls"
            className="h-9 w-full rounded-lg border border-white/20 bg-white/[0.07] pl-9 pr-8 text-[13px] text-text-primary placeholder:text-text-faint"
            placeholder="Search controls by ID, title, or keyword"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button" aria-label="Clear search" onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-text-faint hover:text-text-primary"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <select
          aria-label="Domain" value={domain}
          onChange={(e) => { setDomain(e.target.value); setFamily(''); }}
          className="h-9 rounded-lg border border-border-subtle bg-bg-surface px-2.5 text-[13px] text-text-primary"
        >
          <option value="">All domains</option>
          {domainOptions.map(([c, n]) => <option key={c} value={c}>{c} · {n}</option>)}
        </select>

        <select
          aria-label="Family" value={family} onChange={(e) => setFamily(e.target.value)}
          className="h-9 rounded-lg border border-border-subtle bg-bg-surface px-2.5 text-[13px] text-text-primary"
        >
          <option value="">All families</option>
          {familyOptions.map(([c, n]) => <option key={c} value={c}>{c} · {n}</option>)}
        </select>

        <select
          aria-label="Workspace" value={workspace}
          onChange={(e) => setWorkspace(e.target.value as 'all' | 'in' | 'available')}
          className="h-9 rounded-lg border border-border-subtle bg-bg-surface px-2.5 text-[13px] text-text-primary"
        >
          <option value="all">Workspace: All</option>
          <option value="in">In workspace</option>
          <option value="available">Available to add</option>
        </select>
      </div>

      {filtered && !isLoading && (
        <p className="mb-3 text-[12px] text-text-muted">
          {rows.length} control{rows.length === 1 ? '' : 's'} match ·{' '}
          <button type="button" onClick={reset} className="text-accent-cyan hover:underline">
            Reset
          </button>
        </p>
      )}

      {isLoading ? (
        <LibrarySkeleton />
      ) : (
        <Body
          groups={groups} rows={rows} all={all} filtered={filtered}
          collapsed={collapsed} setCollapsed={setCollapsed} reset={reset}
          onAdd={(ref) => add.mutate(ref)}
          onOpen={openControl}
          adding={add.isPending ? add.variables : undefined}
        />
      )}

      {/* The detail window, when the URL names a control. */}
      <Outlet />
    </div>
  );
}

/**
 * ⚠ THE THREE EMPTY STATES ARE DIFFERENT ANSWERS AND MUST NOT COLLAPSE INTO ONE.
 *
 * Resolution order: no-library → all-added → filter-miss → browse. "Nothing exists yet" is
 * neutral, "you have added everything" is genuinely done, and "your filter matched nothing" is
 * neither — rendering the first as green would congratulate a tenant for an empty catalogue.
 */
function Body({
  groups, rows, all, filtered, collapsed, setCollapsed, reset, onAdd, onOpen, adding,
}: {
  groups: Group[];
  rows: LibraryControl[];
  all: LibraryControl[];
  filtered: boolean;
  collapsed: Record<string, boolean>;
  setCollapsed: (v: Record<string, boolean>) => void;
  reset: () => void;
  onAdd: (ref: string) => void;
  onOpen: (ref: string) => void;
  adding?: string;
}) {
  if (all.length === 0 && !filtered) {
    return (
      <EmptyCard
        icon={BookOpen} testid="empty-no-library"
        title="No controls in the library yet"
        body="Once frameworks are published to your organization, their controls will appear here to browse and add. There's nothing available to add right now."
        link={{ to: '/compliance/workspace/frameworks', label: 'View Frameworks →' }}
      />
    );
  }

  if (!filtered && all.length > 0 && all.every((r) => r.in_workspace)) {
    return (
      <EmptyCard
        icon={CheckCircle2} tone="done" testid="empty-all-added"
        title="All available controls are in your workspace"
        body="Every control published to your organization has been added. New controls will appear here when frameworks are updated."
        link={{ to: '/compliance/workspace/controls', label: 'Go to My Controls →' }}
      />
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyCard
        icon={Search} testid="empty-filter-miss"
        title="No controls match your search"
        body="Try a different term, or clear your search and filters to see the full library."
        action={{ label: 'Reset search & filters', onClick: reset }}
      />
    );
  }

  return (
    <div className="space-y-3">
      {groups.map((d) => {
        const shut = collapsed[d.code];
        return (
          <section
            key={d.code}
            className="overflow-hidden rounded-xl border border-border-subtle bg-bg-surface"
          >
            <button
              type="button"
              onClick={() => setCollapsed({ ...collapsed, [d.code]: !shut })}
              className="flex w-full items-center gap-3 border-b border-border-subtle bg-accent-cyan/[0.06] px-4 py-3 text-left"
            >
              {shut
                ? <ChevronRight className="h-4 w-4 text-text-muted" aria-hidden="true" />
                : <ChevronDown className="h-4 w-4 text-text-muted" aria-hidden="true" />}
              <span className="rounded bg-accent-cyan/15 px-2 py-0.5 font-mono text-[11px] text-accent-cyan">
                {d.code}
              </span>
              <span className="text-[15px] font-semibold text-text-primary">{d.name}</span>
              <span className="ml-auto text-[12px] text-text-muted">
                {d.total} control{d.total === 1 ? '' : 's'} · {d.added} in workspace ·{' '}
                {d.families.length} famil{d.families.length === 1 ? 'y' : 'ies'}
              </span>
            </button>

            {!shut && d.families.map((f) => (
              <div key={f.code}>
                <div className="flex items-center gap-2.5 border-t border-border-subtle bg-white/[0.022] px-4 py-2">
                  <span className="font-mono text-[11px] text-text-muted">{f.code}</span>
                  <span className="text-[13.5px] font-semibold text-[#dbe2ec]">{f.name}</span>
                  <span className="ml-auto text-[11.5px] text-text-muted">
                    {f.rows.length} control{f.rows.length === 1 ? '' : 's'} · {f.added} in workspace
                  </span>
                </div>

                <div className={cn(GRID, 'border-b border-border-subtle bg-white/[0.015] px-4 py-1.5 font-mono text-[10px] uppercase tracking-wider text-text-faint')}>
                  <span>Control ID</span>
                  <span>Control</span>
                  <span>Type</span>
                  <span>Workspace status</span>
                  <span />
                </div>

                {f.rows.map((r) => (
                  // ⚠ THE WHOLE ROW OPENS THE DETAIL WINDOW for a pointer; the TITLE is the real button, so the
                  //   same action is reachable by keyboard and named to a screen reader. The row's own controls
                  //   (Add) act for themselves, and a click that ends a text selection is reading, not opening.
                  <div
                    key={r.control_reference}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button, a, input, select, textarea')) return;
                      if (window.getSelection()?.toString()) return;
                      onOpen(r.control_reference);
                    }}
                    className={cn(GRID, 'cursor-pointer items-start border-t border-border-subtle px-4 py-2.5 hover:bg-white/[0.02]')}
                  >
                    <span className="font-mono text-[12px] text-accent-cyan">{r.display_code}</span>
                    <div className="min-w-0">
                      <button
                        type="button"
                        aria-haspopup="dialog"
                        data-library-open={r.control_reference}
                        onClick={() => onOpen(r.control_reference)}
                        className="block max-w-full truncate rounded-sm text-left text-[14px] font-semibold text-text-primary hover:text-accent-cyan focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan"
                      >
                        {r.title}
                      </button>
                      {r.description && (
                        <div className="truncate text-[12px] text-text-secondary">
                          {r.description}
                        </div>
                      )}
                    </div>
                    <span className="text-[11px] text-text-faint">—</span>
                    <span className="text-[12.5px]">
                      {r.in_workspace ? (
                        <span className="inline-flex items-center gap-1.5 text-cert-valid-fg">
                          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                          In workspace
                        </span>
                      ) : (
                        <span className="text-text-muted">Available to add</span>
                      )}
                    </span>
                    <span>
                      {r.in_workspace ? (
                        <span className="text-text-faint">—</span>
                      ) : (
                        <button
                          type="button"
                          disabled={adding === r.control_reference}
                          onClick={() => onAdd(r.control_reference)}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-accent-cyan px-2.5 py-1.5 text-[12.5px] font-medium text-[#06101f] disabled:opacity-60"
                        >
                          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                          {adding === r.control_reference ? 'Adding…' : 'Add to workspace'}
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
}

function EmptyCard({
  icon: Icon, title, body, link, action, tone, testid,
}: {
  icon: typeof BookOpen;
  title: string;
  body: string;
  link?: { to: string; label: string };
  action?: { label: string; onClick: () => void };
  tone?: 'done';
  testid: string;
}) {
  return (
    <div
      data-testid={testid}
      className={cn(
        'rounded-xl border border-dashed p-10 text-center',
        tone === 'done'
          ? 'border-cert-valid-fg/40 bg-cert-valid-fg/[0.05]'
          : 'border-border-subtle bg-bg-surface',
      )}
    >
      <Icon
        className={cn('mx-auto h-7 w-7', tone === 'done' ? 'text-cert-valid-fg' : 'text-text-faint')}
        aria-hidden="true"
      />
      <p className="mt-3 text-[15px] font-semibold text-text-primary">{title}</p>
      <p className="mx-auto mt-1.5 max-w-md text-[13px] text-text-secondary">{body}</p>
      {link && (
        <Link to={link.to} className="mt-4 inline-block text-[13px] text-accent-cyan hover:underline">
          {link.label}
        </Link>
      )}
      {action && (
        <button
          type="button" onClick={action.onClick}
          className="mt-4 rounded-lg border border-border-subtle px-3 py-1.5 text-[13px] text-text-secondary hover:bg-white/[0.04]"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

/** ⚠ A skeleton over the STRUCTURE, not a spinner — the shape of what is coming is information. */
function LibrarySkeleton() {
  return (
    <div className="space-y-3" data-testid="library-skeleton">
      {[0, 1].map((g) => (
        <section
          key={g}
          className="overflow-hidden rounded-xl border border-border-subtle bg-bg-surface"
        >
          <div className="border-b border-border-subtle bg-accent-cyan/[0.06] px-4 py-3">
            <Skeleton className="h-4 w-56" />
          </div>
          {[0, 1, 2].map((r) => (
            <div
              key={r}
              className={cn(GRID, 'items-center border-t border-border-subtle px-4 py-3')}
            >
              <Skeleton className="h-3.5 w-20" />
              <div className="space-y-1.5">
                <Skeleton className="h-3.5 w-2/3" />
                <Skeleton className="h-3 w-1/3" />
              </div>
              <Skeleton className="h-3.5 w-14" />
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-8 w-full" />
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}
